/**
 * @typedef {Event & Record<string, unknown>} TrackableEvent
 */

/**
 * @typedef {{ event?: Event | null }} TrackOptions
 */

/**
 * @typedef {{
 *   item_id: string,
 *   item_name: string,
 *   item_brand?: string,
 *   item_category?: string,
 *   item_list_name?: string,
 *   price?: number,
 * }} SelectItem
 */

/**
 * @typedef {{
 *   items: SelectItem[],
 *   currency?: string,
 *   item_list_name?: string,
 * }} SelectItemEcommerce
 */

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   vendor: string | null,
 *   type: string | null,
 *   carrusel: string | null,
 *   price?: number,
 *   url?: string,
 * }} SelectItemProduct
 */

/**
 * @typedef {{
 *   event: 'select_item',
 *   ecommerce: SelectItemEcommerce,
 *   product: SelectItemProduct,
 *   product_carrusel?: string,
 *   product_url?: string,
 * }} SelectItemPayload
 */

/**
 * @typedef {{
 *   trackSelectItemFromRoot: (root: Element, options?: TrackOptions) => boolean,
 *   syncSelectItemTracking: (currentRoot: Element, incomingRoot: Element) => void,
 * }} SelectItemAnalyticsApi
 */

/**
 * @typedef {Window & typeof globalThis & {
 *   dataLayer?: Array<Record<string, unknown>>,
 *   TNFSelectItemAnalytics?: SelectItemAnalyticsApi,
 * }} AnalyticsWindow
 */

const TRACKED_EVENT_FLAG = '__tnfSelectItemTracked';

const BLOCKED_CLICK_SELECTOR = [
  'button',
  'input',
  'label',
  'select',
  'textarea',
  'summary',
  '[tabindex="1"]',
  '[data-swatch-overflow-toggle]',
  '[data-popup-close]',
  '[data-card-prev]',
  '[data-card-next]',
  '[data-card-dot]',
  '[data-carousel-prev]',
  '[data-carousel-next]',
  '[data-ww-prev]',
  '[data-ww-next]',
].join(', ');

const TRACKING_ATTRIBUTES = [
  'data-product-id',
  'data-product-title',
  'data-product-vendor',
  'data-product-type',
  'data-product-url',
  'data-current-price-cents',
  'data-currency',
];

/**
 * @returns {AnalyticsWindow}
 */
function getAnalyticsWindow() {
  return window;
}

/**
 * @param {Event} event
 * @returns {Element | null}
 */
function getEventTarget(event) {
  return event.target instanceof Element ? event.target : null;
}

/**
 * @param {Element} root
 * @returns {number | null}
 */
function getRootPriceCents(root) {
  const priceSource = root.querySelector('[data-current-price-cents]')?.getAttribute('data-current-price-cents') || root.getAttribute('data-current-price-cents');
  if (!priceSource) return null;

  const priceCents = Number.parseInt(priceSource, 10);
  return Number.isFinite(priceCents) && priceCents > 0 ? priceCents : null;
}

/**
 * @param {Element} root
 * @returns {string | null}
 */
function getRootCurrency(root) {
  const analyticsWindow = getAnalyticsWindow();
  return (
    root.querySelector('[data-currency]')?.getAttribute('data-currency') ||
    root.getAttribute('data-currency') ||
    analyticsWindow.Shopify?.currency?.active ||
    null
  );
}

/**
 * @param {Element} root
 * @returns {string | null}
 */
function getRootProductId(root) {
  return root.querySelector('product-price[data-product-id]')?.getAttribute('data-product-id') || root.getAttribute('data-product-id');
}

/**
 * @param {Element} root
 * @returns {string | null}
 */
function getRootProductTitle(root) {
  const title =
    root.getAttribute('data-product-title') ||
    root.querySelector('[data-product-title-value]')?.textContent ||
    root.querySelector('[ref="productTitleLink"]')?.textContent ||
    root.querySelector(
      '.tnf-pccarousel__product-name, .ps-card__title, .tnf-bcd__product-title, .tnf-fl__product-title, .tnf-ww__product-name, .tnf-vectiv__product-name'
    )?.textContent ||
    root.querySelector('a[aria-label]')?.getAttribute('aria-label');

  return title?.trim() || null;
}

/**
 * @param {Element} root
 * @returns {string | null}
 */
function getRootListName(root) {
  return root.closest('[data-select-item-list]')?.getAttribute('data-select-item-list') || null;
}

/**
 * @param {Element | null} target
 * @param {Element} root
 * @returns {HTMLAnchorElement | null}
 */
function getTrackedAnchor(target, root) {
  if (!(target instanceof Element) || !(root instanceof Element)) return null;
  if (target.closest(BLOCKED_CLICK_SELECTOR)) return null;

  const clickedAnchor = target.closest('a[href]');
  if (clickedAnchor instanceof HTMLAnchorElement && root.contains(clickedAnchor)) {
    return clickedAnchor;
  }

  if (root instanceof HTMLAnchorElement && root.href) {
    return root;
  }

  return null;
}

/**
 * @param {Element} root
 * @param {HTMLAnchorElement | null} anchor
 * @returns {SelectItemPayload | null}
 */
function buildSelectItemPayload(root, anchor) {
  const itemId = getRootProductId(root);
  const itemName = getRootProductTitle(root);
  if (!itemId || !itemName) return null;

  const itemListName = getRootListName(root);
  const itemBrand = root.getAttribute('data-product-vendor');
  const itemCategory = root.getAttribute('data-product-type');
  const priceCents = getRootPriceCents(root);
  const currency = getRootCurrency(root);

  /** @type {SelectItem} */
  const item = {
    item_id: itemId,
    item_name: itemName,
  };

  if (itemBrand) item.item_brand = itemBrand;
  if (itemCategory) item.item_category = itemCategory;
  if (itemListName) item.item_list_name = itemListName;
  if (priceCents != null) item.price = priceCents / 100;

  /** @type {SelectItemEcommerce} */
  const ecommerce = {
    items: [item],
  };

  if (currency) ecommerce.currency = currency;
  if (itemListName) ecommerce.item_list_name = itemListName;

  /** @type {SelectItemPayload} */
  const payload = {
    event: 'select_item',
    ecommerce,
    product: {
      id: itemId,
      title: itemName,
      vendor: itemBrand || null,
      type: itemCategory || null,
      carrusel: itemListName || null,
    },
  };

  if (priceCents != null) {
    payload.product.price = priceCents / 100;
  }

  if (itemListName) {
    payload.product_carrusel = itemListName;
  }

  const productUrl = root.getAttribute('data-product-url') || anchor?.href;
  if (productUrl) {
    payload.product_url = productUrl;
    payload.product.url = productUrl;
  }

  return payload;
}

/**
 * @param {Element} root
 * @param {TrackOptions} [options]
 * @returns {boolean}
 */
export function trackSelectItemFromRoot(root, options = {}) {
  if (!(root instanceof Element)) return false;

  const { event } = options;
  const trackedEvent = event ? /** @type {TrackableEvent} */ (event) : null;
  if (trackedEvent && trackedEvent[TRACKED_EVENT_FLAG]) return false;

  const target = event ? getEventTarget(event) : root;
  const anchor = getTrackedAnchor(target, root);
  if (!anchor && !(root instanceof HTMLElement && root.tagName.toLowerCase() === 'product-card')) {
    return false;
  }

  const payload = buildSelectItemPayload(root, anchor);
  if (!payload) return false;

  if (trackedEvent) {
    trackedEvent[TRACKED_EVENT_FLAG] = true;
  }

  const analyticsWindow = getAnalyticsWindow();
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.dataLayer.push({ ecommerce: null });
  analyticsWindow.dataLayer.push(payload);
  return true;
}

/**
 * @param {Element} currentRoot
 * @param {Element} incomingRoot
 * @returns {void}
 */
export function syncSelectItemTracking(currentRoot, incomingRoot) {
  if (!(currentRoot instanceof Element) || !(incomingRoot instanceof Element)) return;

  TRACKING_ATTRIBUTES.forEach((attributeName) => {
    const value = incomingRoot.getAttribute(attributeName);
    if (value == null) {
      currentRoot.removeAttribute(attributeName);
      return;
    }

    currentRoot.setAttribute(attributeName, value);
  });
}

if (typeof window !== 'undefined') {
  const analyticsWindow = getAnalyticsWindow();
  analyticsWindow.TNFSelectItemAnalytics = Object.assign(analyticsWindow.TNFSelectItemAnalytics || {}, {
    trackSelectItemFromRoot,
    syncSelectItemTracking,
  });
}

/**
 * @param {Event} event
 * @returns {void}
 */
function handleDelegatedSelectItem(event) {
  const target = getEventTarget(event);
  if (!target) return;

  const root = target.closest('[data-select-item-root]');
  if (!(root instanceof Element)) return;
  if (root.closest('product-card')) return;

  trackSelectItemFromRoot(root, { event });
}

document.addEventListener('click', handleDelegatedSelectItem, { capture: true });